import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../supabase/supabaseClient'
import { convertOdds } from '../utils/oddsConverter'
import { BOOKMAKER_LOGOS, BOOKMAKERS_BY_REGION as BOOKMAKERS } from '../config/bookmakers'
import { ALL_SPORTS as SPORTS, eventLabel, eventLabelShort } from '../config/sports'
import { MARKET_TYPE_MAPPING, SELECTION_SHORT_NAMES } from '../config/markets'
import { saveUserFilter, getUserFilters, deleteUserFilter, setDefaultFilter, unsetDefaultFilter, getDefaultFilter } from '../services/userDataService'
import '../styles/app.css'
import '../ARB_NEW_PAGE/ArbNew.css'
import './BonusNew.css'

import FilterPanel from './FilterPanel'
import HiddenBetsModal from '../ARB_NEW_PAGE/HiddenBetsModal'
import BonusTour, { openBonusTour } from './bonusTour'
import TrackBetModal, { useBonusTrack } from './TrackBetModal'
import PreviewWall from '../components/PreviewWall'
import useVisiblePolling from '../hooks/useVisiblePolling'

export default function BonusNew({ isVisible = true, isPreview: isPreviewProp = false }) {
  const { user, oddsFormat, hasAccess, hadTrial, loading: authLoading } = useAuth()
  const isPreview = isPreviewProp || authLoading || !hasAccess('bonus')

  // ─── Data ────────────────────────────────────────────────────────────────
  const [opportunities, setOpportunities]           = useState([])
  const [loading, setLoading]                       = useState(true)
  const [error, setError]                           = useState(null)
  const [lastUpdate, setLastUpdate]                 = useState(null)
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false)

  // ─── Bonus amount ────────────────────────────────────────────────────────
  const [defaultBonusAmount, setDefaultBonusAmount] = useState(50)

  // ─── Filters ─────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState({
    sports:          new Set(),
    bonusBookmakers: new Set(),
    hedgeBookmakers: new Set(),
    odds:            [1, 6],
    retention:       [65, 100],
    hoursUntil:      [0, 72],
  })
  const [savedFilters, setSavedFilters]               = useState([])
  const [filterName, setFilterName]                   = useState('')
  const [activeFilterId, setActiveFilterId]           = useState(null)
  const [defaultFilterLoaded, setDefaultFilterLoaded] = useState(false)
  const [showFilterPanel, setShowFilterPanel]         = useState(false)

  // ─── Sort + pagination ───────────────────────────────────────────────────
  const [sortBy, setSortBy]           = useState('retention_percentage')
  const [sortDir, setSortDir]         = useState('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 25

  // ─── Row state ───────────────────────────────────────────────────────────
  const [expandedRows, setExpandedRows] = useState(new Set())
  const [editableData, setEditableData] = useState({})

  // ─── Hidden bets ─────────────────────────────────────────────────────────
  const [hiddenBets, setHiddenBets]           = useState({})
  const [showHiddenModal, setShowHiddenModal] = useState(false)

  // ─── Modals ──────────────────────────────────────────────────────────────

  // ─── Live-dot tick ───────────────────────────────────────────────────────
  // eslint-disable-next-line no-unused-vars
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 10000)
    return () => clearInterval(id)
  }, [])

  const dataAgeSec = lastUpdate ? (new Date() - lastUpdate) / 1000 : null
  const dotClass   = !lastUpdate ? 'bc-live-dot' : dataAgeSec < 60 ? 'bc-live-dot' : 'bc-live-dot stale'

  const formatTimeWithSeconds = (dateString) => {
    if (!dateString) return ''
    return new Date(dateString + 'Z').toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })
  }

  // ─── Fetch ────────────────────────────────────────────────────────────────
  const fetchOpportunities = async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      const { data, error } = await supabase.functions.invoke('bonus-proxy')
      if (error) throw error
      setOpportunities(data.opportunities || [])
      setLastUpdate(new Date())
      setError(null)
    } catch (err) {
      setError(err.message)
    } finally {
      if (!silent) setLoading(false)
    }
  }

  useEffect(() => { fetchOpportunities() }, [])

  useVisiblePolling(() => fetchOpportunities(true), 30000, autoRefreshEnabled && isVisible)

  // ─── Hidden bets bootstrap ───────────────────────────────────────────────
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('hidden-bonus-bets') || '{}')
    const now = new Date()
    const cleaned = {}
    Object.entries(stored).forEach(([id, data]) => {
      const gameTime = new Date(data.commence_time + 'Z')
      if (now < new Date(gameTime.getTime() + 3600000)) cleaned[id] = data
    })
    localStorage.setItem('hidden-bonus-bets', JSON.stringify(cleaned))
    setHiddenBets(cleaned)
  }, [])

  // ─── Filter persistence ──────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.id) {
      setFilters(prev => ({ ...prev, sports: new Set(SPORTS), bonusBookmakers: new Set(BOOKMAKERS.AU), hedgeBookmakers: new Set(BOOKMAKERS.AU) }))
      return
    }
    getUserFilters(user.id, 'bonus').then(({ data }) => {
      if (data) setSavedFilters(data.map(f => ({ id: f.id, name: f.filter_name, isDefault: f.is_default || false, ...f.filter_data })))
    })
  }, [user?.id])

  useEffect(() => {
    if (!user?.id || defaultFilterLoaded) return
    getDefaultFilter(user.id, 'bonus').then(({ data }) => {
      if (data?.filter_data) {
        applyFilterData(data.filter_data)
      } else {
        setFilters(prev => ({ ...prev, sports: new Set(SPORTS), bonusBookmakers: new Set(BOOKMAKERS.AU), hedgeBookmakers: new Set(BOOKMAKERS.AU) }))
      }
      setDefaultFilterLoaded(true)
    })
  }, [user?.id, defaultFilterLoaded])

  const applyFilterData = (fd) => {
    // Prefer the saved excludedSports blacklist (newer filters) so leagues added after the
    // filter was saved stay visible (selected = all minus excluded). Fall back to the legacy
    // sports whitelist for old filters.
    const selectedSports = Array.isArray(fd.filters.excludedSports)
      ? SPORTS.filter(s => !fd.filters.excludedSports.includes(s))
      : (fd.filters.sports || SPORTS)
    setFilters({
      sports:          new Set(selectedSports),
      bonusBookmakers: new Set(fd.filters.bonusBookmakers || BOOKMAKERS.AU),
      hedgeBookmakers: new Set(fd.filters.hedgeBookmakers || BOOKMAKERS.AU),
      odds:            fd.filters.odds      || [1, 6],
      retention:       fd.filters.retention || [65, 100],
      hoursUntil:      fd.filters.hoursUntil || [0, 72],
    })
    if (fd.bonusSettings) {
      setDefaultBonusAmount(fd.bonusSettings.defaultBonusAmount ?? 50)
    }
  }

  const handleSaveFilter = async (name) => {
    if (!name.trim() || !user?.id) return
    const fd = {
      filters: {
        sports:          Array.from(filters.sports),                     // legacy whitelist (back-compat)
        excludedSports:  SPORTS.filter(s => !filters.sports.has(s)),     // blacklist = source of truth on load
        bonusBookmakers: Array.from(filters.bonusBookmakers),
        hedgeBookmakers: Array.from(filters.hedgeBookmakers),
        odds:            filters.odds,
        retention:       filters.retention,
        hoursUntil:      filters.hoursUntil,
      },
      bonusSettings: { defaultBonusAmount },
      createdAt: new Date().toISOString(),
    }
    const { data, error } = await saveUserFilter(user.id, 'bonus', name.trim(), fd)
    if (error) { alert('Error saving filter: ' + error); return }
    if (data) setSavedFilters(prev => [...prev, { id: data.id, name: data.filter_name, isDefault: false, ...data.filter_data }])
    setFilterName('')
  }

  const handleLoadFilter = (saved) => {
    applyFilterData(saved)
    setActiveFilterId(saved.id)
  }

  const handleDeleteFilter = async (id) => {
    if (!user?.id) return
    const { error } = await deleteUserFilter(id)
    if (error) { alert('Error deleting filter: ' + error); return }
    setSavedFilters(prev => prev.filter(f => f.id !== id))
    if (activeFilterId === id) setActiveFilterId(null)
  }

  const handleToggleDefault = async (id) => {
    if (!user?.id) return
    const current = savedFilters.find(f => f.id === id)
    if (current?.isDefault) {
      const { error } = await unsetDefaultFilter(user.id, 'bonus', id)
      if (error) { alert('Error unsetting default: ' + error); return }
    } else {
      const { error } = await setDefaultFilter(user.id, 'bonus', id)
      if (error) { alert('Error setting default: ' + error); return }
    }
    setSavedFilters(prev => prev.map(f => ({ ...f, isDefault: f.id === id ? !current?.isDefault : false })))
  }

  const resetFilters = () => {
    setFilters({ sports: new Set(SPORTS), bonusBookmakers: new Set(BOOKMAKERS.AU), hedgeBookmakers: new Set(BOOKMAKERS.AU), odds: [1, 6], retention: [65, 100], hoursUntil: [0, 24] })
    setDefaultBonusAmount(50)
  }

  // ─── Bonus calculation ────────────────────────────────────────────────────
  // Stake not returned on BONUS leg; HEDGE is a normal lay.
  // bonusWinnings = bonusAmount * (bonusOdds - 1)  [no stake returned]
  // hedgeStake    = bonusWinnings / hedgeOdds       [ensures both scenarios equal]
  // bonusWinProfit = bonusWinnings - hedgeStake
  // hedgeWinProfit = hedgeStake * (hedgeOdds - 1)
  // guaranteedProfit = min(bonusWinProfit, hedgeWinProfit)
  const calculateBonusConversion = (outcomes, customBonusAmount = null) => {
    if (!outcomes || outcomes.length !== 2) return { bonusStake: 0, hedgeStake: 0, guaranteedProfit: 0, retentionPercentage: 0, bonusWinProfit: 0, hedgeWinProfit: 0 }
    const bonusOutcome = outcomes.find(o => o.bet_type === 'BONUS')
    const hedgeOutcome = outcomes.find(o => o.bet_type === 'HEDGE')
    if (!bonusOutcome || !hedgeOutcome) return { bonusStake: 0, hedgeStake: 0, guaranteedProfit: 0, retentionPercentage: 0, bonusWinProfit: 0, hedgeWinProfit: 0 }

    const bonusAmount   = customBonusAmount ?? defaultBonusAmount
    const bonusOdds     = bonusOutcome.odds
    const hedgeOdds     = hedgeOutcome.odds
    const bonusWinnings = bonusAmount * (bonusOdds - 1)
    const hedgeStake    = bonusWinnings / hedgeOdds
    const bonusWinProfit = bonusWinnings - hedgeStake
    const hedgeWinProfit = hedgeStake * (hedgeOdds - 1)
    const guaranteedProfit = Math.min(bonusWinProfit, hedgeWinProfit)
    const retentionPercentage = (guaranteedProfit / bonusAmount) * 100

    return {
      bonusStake:          bonusAmount,
      hedgeStake:          Math.round(hedgeStake * 100) / 100,
      guaranteedProfit:    Math.round(guaranteedProfit * 100) / 100,
      retentionPercentage: Math.round(retentionPercentage * 100) / 100,
      bonusWinProfit:      Math.round(bonusWinProfit * 100) / 100,
      hedgeWinProfit:      Math.round(hedgeWinProfit * 100) / 100,
    }
  }

  const retentionBadgeClass = (r) => {
    if (r >= 80) return 'bc-ev-badge retention-badge-gold'
    if (r >= 70) return 'bc-ev-badge retention-badge-green'
    if (r >= 60) return 'bc-ev-badge retention-badge-blue'
    return 'bc-ev-badge retention-badge-red'
  }

  // ─── Editable data helpers ────────────────────────────────────────────────
  const getEditableOdds = (oppId, idx, defaultVal) => {
    const d = editableData[oppId]
    return d?.odds?.[idx] !== undefined ? d.odds[idx] : defaultVal
  }
  const getEditableOddsRaw = (oppId, idx, defaultVal) => {
    const d = editableData[oppId]
    return d?.oddsRaw?.[idx] !== undefined ? d.oddsRaw[idx] : String(defaultVal)
  }
  const updateOdds = (oppId, idx, val) => {
    setEditableData(prev => {
      const d = { ...(prev[oppId] || {}) }
      d.odds    = [...(d.odds    || [])]
      d.oddsRaw = [...(d.oddsRaw || [])]
      d.oddsRaw[idx] = val
      const num = parseFloat(val)
      d.odds[idx] = isNaN(num) ? d.odds[idx] : num
      return { ...prev, [oppId]: d }
    })
  }
  const getEditableBonusAmount = (oppId) => {
    const d = editableData[oppId]
    return d?.bonusAmount !== undefined ? d.bonusAmount : defaultBonusAmount
  }
  const getEditableBonusAmountRaw = (oppId) => {
    const d = editableData[oppId]
    return d?.bonusAmountRaw !== undefined ? d.bonusAmountRaw : String(defaultBonusAmount)
  }
  const updateBonusAmount = (oppId, val) => {
    setEditableData(prev => {
      const num = parseFloat(val)
      return { ...prev, [oppId]: { ...(prev[oppId] || {}), bonusAmountRaw: val, bonusAmount: isNaN(num) ? (prev[oppId]?.bonusAmount ?? defaultBonusAmount) : num } }
    })
  }

  // ─── Format helpers ──────────────────────────────────────────────────────
  const formatDateTime = (ds) => new Date(ds + 'Z').toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
  const getHoursUntil  = (ct) => Math.max(0, (new Date(ct + 'Z') - new Date()) / 3600000)

  const formatMarketType = (marketType, playerName = '') => {
    if (MARKET_TYPE_MAPPING[marketType]) return MARKET_TYPE_MAPPING[marketType]
    if (playerName && marketType.includes('player_')) {
      return `Player Prop - ${marketType.replace('player_', '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`
    }
    return marketType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const formatSelection = (selection, marketType, playerName = '') => {
    if (SELECTION_SHORT_NAMES[marketType]) {
      const parts = selection.split(' ')
      for (let i = 0; i < parts.length; i++) {
        if ((parts[i].toLowerCase() === 'over' || parts[i].toLowerCase() === 'under') && i + 1 < parts.length && parts[i + 1].replace('.', '').replace('-', '').match(/^\d+$/)) {
          return playerName
            ? `${playerName} ${parts[i]} ${parts[i + 1]} ${SELECTION_SHORT_NAMES[marketType]}`
            : `${parts[i]} ${parts[i + 1]} ${SELECTION_SHORT_NAMES[marketType]}`
        }
      }
    }
    if (marketType === 'alternate_lines') {
      const parts = selection.split(' ')
      if (parts.length >= 2) {
        const team = parts.slice(0, -1).join(' ')
        let hcap = parts[parts.length - 1]
        if (hcap[0] !== '-' && hcap[0] !== '+') hcap = `+${hcap}`
        return `${team} ${hcap}`
      }
    }
    return selection
  }

  // ─── Sort ────────────────────────────────────────────────────────────────
  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc')
    else { setSortBy(col); setSortDir('desc') }
  }

  // ─── Filter + sort ───────────────────────────────────────────────────────
  const filteredOpportunities = useMemo(() => {
    const filtered = opportunities.filter(opp => {
      if (filters.sports.size > 0 && !filters.sports.has(opp.sport)) return false

      const bonusOutcome = opp.outcomes?.find(o => o.bet_type === 'BONUS')
      const hedgeOutcome = opp.outcomes?.find(o => o.bet_type === 'HEDGE')
      if (bonusOutcome && filters.bonusBookmakers.size > 0 && !filters.bonusBookmakers.has(bonusOutcome.bookmaker)) return false
      if (hedgeOutcome && filters.hedgeBookmakers.size > 0 && !filters.hedgeBookmakers.has(hedgeOutcome.bookmaker)) return false

      if (opp.outcomes?.some(o => o.odds < filters.odds[0] || o.odds > filters.odds[1])) return false

      const retention = calculateBonusConversion(opp.outcomes, defaultBonusAmount).retentionPercentage
      if (retention < filters.retention[0] || retention > filters.retention[1]) return false

      const hrs = getHoursUntil(opp.commence_time)
      if (hrs < filters.hoursUntil[0] || hrs > filters.hoursUntil[1]) return false

      return true
    })

    return filtered.sort((a, b) => {
      let av, bv
      if (sortBy === 'retention_percentage') {
        av = calculateBonusConversion(a.outcomes, defaultBonusAmount).retentionPercentage
        bv = calculateBonusConversion(b.outcomes, defaultBonusAmount).retentionPercentage
      } else if (sortBy === 'odds') {
        av = a.outcomes ? a.outcomes.reduce((s, o) => s + o.odds, 0) / a.outcomes.length : 0
        bv = b.outcomes ? b.outcomes.reduce((s, o) => s + o.odds, 0) / b.outcomes.length : 0
      } else if (sortBy === 'start_time') {
        av = new Date(a.commence_time).getTime()
        bv = new Date(b.commence_time).getTime()
      } else if (sortBy === 'sport') {
        return sortDir === 'desc' ? b.sport.localeCompare(a.sport) : a.sport.localeCompare(b.sport)
      } else { av = 0; bv = 0 }
      return sortDir === 'desc' ? bv - av : av - bv
    })
    // calculateBonusConversion is an inline fn (new identity each render); adding it would defeat the memo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunities, filters, defaultBonusAmount, sortBy, sortDir])

  // ─── Hidden bets ─────────────────────────────────────────────────────────
  function generateHideId(opp) {
    const s1 = opp.outcomes?.[0]?.selection || ''
    const s2 = opp.outcomes?.[1]?.selection || ''
    const b1 = opp.outcomes?.[0]?.bookmaker || ''
    const b2 = opp.outcomes?.[1]?.bookmaker || ''
    return `${opp.sport}_${opp.match}_${opp.market_type}_${s1}_${s2}_${b1}_${b2}_${opp.commence_time}`.replace(/[^a-zA-Z0-9_]/g, '_')
  }

  const hideOpportunity = (opp) => {
    const id = generateHideId(opp)
    const updated = {
      ...hiddenBets,
      [id]: { match: opp.match, selection1: opp.outcomes?.[0]?.selection, selection2: opp.outcomes?.[1]?.selection, bookmaker1: opp.outcomes?.[0]?.bookmaker, bookmaker2: opp.outcomes?.[1]?.bookmaker, commence_time: opp.commence_time, sport: opp.sport, market_type: opp.market_type },
    }
    localStorage.setItem('hidden-bonus-bets', JSON.stringify(updated))
    setHiddenBets(updated)
  }

  const unhideOpportunity = (betId) => {
    const updated = { ...hiddenBets }
    delete updated[betId]
    localStorage.setItem('hidden-bonus-bets', JSON.stringify(updated))
    setHiddenBets(updated)
  }

  const clearAllHidden = () => {
    localStorage.removeItem('hidden-bonus-bets')
    setHiddenBets({})
  }

  // ─── Track ───────────────────────────────────────────────────────────────
  const track = useBonusTrack({ hideOpportunity })

  // ─── Visible + paged ─────────────────────────────────────────────────────
  const visibleOpportunities = useMemo(
    () => filteredOpportunities.filter(opp => !hiddenBets[generateHideId(opp)]),
    [filteredOpportunities, hiddenBets]
  )

  const totalPages = Math.max(1, Math.ceil(visibleOpportunities.length / itemsPerPage))
  useEffect(() => { if (currentPage > totalPages) setCurrentPage(1) }, [totalPages, currentPage])
  useEffect(() => { setCurrentPage(1) }, [filters, sortBy, sortDir])

  const pagedOpps = isPreview
    ? visibleOpportunities.slice(0, 3)
    : visibleOpportunities.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  // ─── Row expand ──────────────────────────────────────────────────────────
  const toggleExpand = (id) => setExpandedRows(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  if (!isVisible) return null

  const hiddenCount = Object.keys(hiddenBets).length

  return (
    <div className="bc-container tool-unified tool-refined bonus-unified">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="bc-header">
        <div className="bc-header-titlewrap">
          <div className="bc-header-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 12 20 22 4 22 4 12"/>
              <rect x="2" y="7" width="20" height="5"/>
              <line x1="12" y1="22" x2="12" y2="7"/>
              <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
              <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
            </svg>
          </div>
          <div className="bc-header-textcol">
            <div className="bc-header-titlerow">
              <h1 className="bc-title">Bonus Converter</h1>
              <button className="bc-info-btn" onClick={openBonusTour} aria-label="How this works" data-tooltip="How this works">?</button>
              <span
                className={dotClass}
                data-tooltip={lastUpdate ? `Last refreshed ${formatTimeWithSeconds(lastUpdate.toISOString().replace('Z', ''))}` : 'No data yet'}
              />
            </div>
          </div>
        </div>

        <div className="bc-header-actions">
          <button className="bc-btn" onClick={() => setShowFilterPanel(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
            Filters
          </button>

          {hiddenCount > 0 && (
            <button className="bc-btn" onClick={() => setShowHiddenModal(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
              {hiddenCount}
            </button>
          )}

          <button
            className={`bc-btn bc-btn-toggle${autoRefreshEnabled ? ' toggle-active' : ''}`}
            onClick={() => setAutoRefreshEnabled(v => !v)}
          >
            Auto
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
          </button>

          <button className="bc-btn bc-icon-btn" onClick={() => fetchOpportunities()} disabled={loading}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={loading ? 'bc-spin' : ''}>
              <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
          </button>
        </div>
      </div>

      {error && <div className="bc-error">Error: {error}</div>}

      {loading && (
        <div className="bc-loading">
          <div className="bc-spinner" />
        </div>
      )}

      {!loading && (
        <div className="bc-table-wrapper">
          <div className="bc-table-scroll">
            <div className="arb-grid-table">

              {/* ── Header strip ─────────────────────────────────────────── */}
              <div className="arb-grid-header bonus-grid-header">
                {[
                  { label: 'Match',      key: 'start_time',           divider: false },
                  { label: 'Market',     key: null,                   divider: false },
                  { label: 'Outcomes',   key: 'odds',                 divider: 'right' },
                  { label: 'Stakes',     key: null,                   divider: false },
                  { label: 'Retention%', key: 'retention_percentage', divider: false },
                  { label: '',           key: null,                   divider: false },
                ].map((col, i) => (
                  <div
                    key={i}
                    className={`arb-grid-header-cell${col.key ? ' sortable' : ''}${sortBy === col.key ? ' sorted' : ''}${col.divider === 'right' ? ' divider-right' : ''}`}
                    onClick={() => col.key && handleSort(col.key)}
                  >
                    {col.label}
                    {col.key && (
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        {sortBy === col.key
                          ? sortDir === 'desc' ? <polyline points="6 9 12 15 18 9"/> : <polyline points="18 15 12 9 6 15"/>
                          : <><polyline points="6 9 12 4 18 9"/><polyline points="6 15 12 20 18 15"/></>
                        }
                      </svg>
                    )}
                  </div>
                ))}
              </div>

              {/* ── Rows ─────────────────────────────────────────────────── */}
              <div className="arb-grid-rows">
                {pagedOpps.map((opp, idx) => {
                  const rowId      = opp._id
                  const isExpanded = expandedRows.has(rowId)
                  const blurRow    = isPreview && idx > 0
                  const blurStyle  = blurRow ? { filter: 'blur(5px)', userSelect: 'none', pointerEvents: 'none' } : {}
                  const blurDetail = isPreview && idx === 0

                  const editedBonusAmount = getEditableBonusAmount(rowId)

                  const customOutcomes = opp.outcomes?.map((o, oi) => ({ ...o, odds: getEditableOdds(rowId, oi, o.odds) }))
                  const calc = calculateBonusConversion(customOutcomes, editedBonusAmount)

                  return (
                    <div key={rowId} className={`arb-grid-row-wrap${isExpanded ? ' expanded' : ''}`}>

                      {/* Main row */}
                      <div className="arb-grid-row bonus-grid-row" style={blurStyle}>

                        {/* Match, then sport tag + kick-off beneath it */}
                        <div className="arb-grid-cell">
                          <div className="arb-match-name">{opp.match}</div>
                          <div className="bc-match-meta">
                            <span className="bc-sport-tag" title={eventLabel(opp)}>{eventLabelShort(opp)}</span>
                            <span className="arb-match-time">{formatDateTime(opp.commence_time)}</span>
                          </div>
                        </div>

                        {/* Market */}
                        <div className="arb-grid-cell">
                          <span className="arb-market-text">{formatMarketType(opp.market_type, opp.player_name)}</span>
                        </div>

                        {/* Outcomes */}
                        <div className="arb-grid-cell divider-right" style={blurDetail ? { filter: 'blur(6px)', userSelect: 'none', pointerEvents: 'none' } : {}}>
                          <div className="arb-outcomes-box">
                            {opp.outcomes?.map((outcome, oi) => {
                              const logo = BOOKMAKER_LOGOS[outcome.bookmaker]
                              const abbr = outcome.bookmaker.slice(0, 2).toUpperCase()
                              const isBonus = outcome.bet_type === 'BONUS'
                              return (
                                <div key={oi} className={`arb-leg-row ${isBonus ? 'bonus-leg-row-bonus' : 'bonus-leg-row-hedge'}`}>
                                  <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                                    <span className={`bonus-leg-label ${isBonus ? 'bonus-leg-label-bonus' : 'bonus-leg-label-hedge'}`}>
                                      {isBonus ? 'Bonus' : 'Hedge'}
                                    </span>
                                    <span className="arb-leg-sel">{formatSelection(outcome.selection, opp.market_type, opp.player_name)}</span>
                                  </div>
                                  <div className="arb-leg-book-odds">
                                    <span
                                      className="arb-leg-book"
                                      data-tooltip={outcome.bookmaker}
                                      onClick={() => outcome.game_url && window.open(outcome.game_url, '_blank')}
                                      style={{ cursor: outcome.game_url ? 'pointer' : 'default' }}
                                    >
                                      {logo
                                        ? <img src={logo} alt={abbr} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
                                        : abbr
                                      }
                                    </span>
                                    <span className="arb-leg-odds">{convertOdds(getEditableOdds(rowId, oi, outcome.odds), oddsFormat)}</span>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>

                        {/* Stakes */}
                        <div className="arb-grid-cell stakes-cell" style={blurStyle}>
                          <div className="arb-stake-val">${calc.bonusStake}</div>
                          <div className="arb-stake-val">${calc.hedgeStake}</div>
                        </div>

                        {/* Retention% */}
                        <div className="arb-grid-cell" style={blurStyle}>
                          <span className={retentionBadgeClass(calc.retentionPercentage)}>
                            {calc.retentionPercentage.toFixed(2)}%
                          </span>
                        </div>

                        {/* Actions */}
                        <div className="arb-grid-cell arb-actions-cell" style={blurStyle}>
                          {!isPreview && (
                            <button
                              className={`arb-action-btn track-btn${track.isTracked(opp) ? ' tracked' : ''}`}
                              onClick={() => !track.isTracked(opp) && track.open(opp, customOutcomes, editedBonusAmount)}
                              data-tooltip={track.isTracked(opp) ? 'Tracked' : 'Track bet'}
                            >
                              {track.isTracked(opp)
                                ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                              }
                            </button>
                          )}
                          {!isPreview && (
                            <button
                              className="arb-action-btn hide-btn"
                              onClick={() => hideOpportunity(opp)}
                              data-tooltip="Hide"
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                                <line x1="1" y1="1" x2="23" y2="23"/>
                              </svg>
                            </button>
                          )}
                          <button
                            className={`arb-action-btn expand-btn${isPreview ? ' locked' : ''}${isExpanded ? ' active' : ''}`}
                            onClick={() => !isPreview && toggleExpand(rowId)}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              {isExpanded ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* ── Expanded panel ──────────────────────────────── */}
                      {isExpanded && (
                        <div className="arb-expanded-panel">

                          {/* Bonus amount top bar */}
                          <div className="arb-exp-topbar">
                            <div className="arb-exp-topbar-stake">
                              <span className="arb-exp-topbar-label">Bonus Amount</span>
                              <div className="arb-exp-topbar-input-wrap">
                                <span className="arb-exp-topbar-prefix">$</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={getEditableBonusAmountRaw(rowId)}
                                  onChange={e => updateBonusAmount(rowId, e.target.value)}
                                  className="arb-exp-stake-input"
                                />
                              </div>
                            </div>
                            <div className="arb-exp-topbar-stake">
                              <span className="arb-exp-topbar-label">Guaranteed Profit</span>
                              <span className="arb-outcome-field-profit-pos" style={{ fontSize: '13px', fontWeight: 700 }}>${calc.guaranteedProfit}</span>
                            </div>
                            <div className="arb-exp-topbar-stake">
                              <span className="arb-exp-topbar-label">Retention</span>
                              <span className={retentionBadgeClass(calc.retentionPercentage)} style={{ fontSize: '13px', fontWeight: 700 }}>{calc.retentionPercentage.toFixed(1)}%</span>
                            </div>
                          </div>

                          {/* Outcome cards */}
                          <div className="arb-outcome-cards-row">
                            {opp.outcomes?.map((outcome, oi) => {
                              const logo = BOOKMAKER_LOGOS[outcome.bookmaker]
                              const isBonus = outcome.bet_type === 'BONUS'
                              const winProfit = isBonus ? calc.bonusWinProfit : calc.hedgeWinProfit
                              const stakeDisplay = isBonus ? calc.bonusStake : calc.hedgeStake
                              return (
                                <div key={oi} className="arb-outcome-card">
                                  <div className="arb-outcome-card-header">
                                    {logo
                                      ? <img src={logo} alt={outcome.bookmaker} className="arb-outcome-card-logo" />
                                      : <div className="arb-outcome-card-logo-placeholder">{outcome.bookmaker.slice(0, 3).toUpperCase()}</div>
                                    }
                                    <div>
                                      <div className="arb-outcome-card-name">
                                        {outcome.bookmaker}
                                        <span className={`bonus-leg-label ${isBonus ? 'bonus-leg-label-bonus' : 'bonus-leg-label-hedge'}`} style={{ marginLeft: '6px' }}>
                                          {isBonus ? 'Bonus' : 'Hedge'}
                                        </span>
                                      </div>
                                      <div className="arb-outcome-card-sel">{formatSelection(outcome.selection, opp.market_type, opp.player_name)}</div>
                                    </div>
                                  </div>

                                  <div className="arb-outcome-field">
                                    <span className="arb-outcome-field-label">Odds</span>
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      value={getEditableOddsRaw(rowId, oi, outcome.odds)}
                                      onChange={e => updateOdds(rowId, oi, e.target.value)}
                                      className="arb-outcome-odds-input"
                                    />
                                    {oddsFormat !== 'decimal' && getEditableOdds(rowId, oi, outcome.odds) > 0 && (
                                      <span style={{ fontSize: '11px', color: '#64748b' }}>({convertOdds(getEditableOdds(rowId, oi, outcome.odds), oddsFormat)})</span>
                                    )}
                                  </div>

                                  <div className="arb-outcome-field">
                                    <span className="arb-outcome-field-label">Stake</span>
                                    <span className="arb-outcome-field-val">${stakeDisplay}</span>
                                  </div>

                                  <div className="arb-outcome-field">
                                    <span className="arb-outcome-field-label">If wins</span>
                                    <span className={typeof winProfit === 'number' && winProfit >= 0 ? 'arb-outcome-field-profit-pos' : 'arb-outcome-field-profit-neg'}>
                                      {typeof winProfit === 'number' ? `$${winProfit}` : '—'}
                                    </span>
                                  </div>

                                  <button className="arb-open-btn" onClick={() => outcome.game_url && window.open(outcome.game_url, '_blank')} disabled={!outcome.game_url}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                                      <polyline points="15 3 21 3 21 9"/>
                                      <line x1="10" y1="14" x2="21" y2="3"/>
                                    </svg>
                                    Open {outcome.bookmaker}
                                  </button>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Preview fade */}
            {isPreview && <div className="bc-preview-fade" />}
          </div>

          {/* Preview wall */}
          {isPreview && (
            <div style={{ padding: '28px 16px 36px', display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: '100%', maxWidth: '520px' }}>
                <PreviewWall
                  title={visibleOpportunities.length > 0 ? `${visibleOpportunities.length} bonus conversion opportunities right now` : 'Bonus conversion opportunities right now'}
                  features={['Convert bonus bets to guaranteed profit', 'Real-time odds from all major bookmakers', 'Automatic hedge stake calculation', 'Much more...',]}
                  planName="Promo Premium"
                  accent="#a78bfa"
                  hadTrial={hadTrial}
                />
              </div>
            </div>
          )}

          {!isPreview && !loading && visibleOpportunities.length === 0 && (
            <div className="bc-empty">No bonus opportunities match your filters.</div>
          )}
        </div>
      )}

      {/* ── Pagination ─────────────────────────────────────────────────── */}
      {!isPreview && !loading && totalPages > 1 && (
        <div className="bc-pagination">
          <button className="bc-page-arrow" onClick={() => { setCurrentPage(1); window.scrollTo({ top: 0 }) }} disabled={currentPage === 1} data-tooltip="First page">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="11 18 5 12 11 6"/><polyline points="19 18 13 12 19 6"/></svg>
          </button>
          <button className="bc-page-arrow" onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0 }) }} disabled={currentPage === 1} data-tooltip="Previous page">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <span className="bc-page-info">{currentPage} / {totalPages}</span>
          <button className="bc-page-arrow" onClick={() => { setCurrentPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0 }) }} disabled={currentPage === totalPages} data-tooltip="Next page">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <button className="bc-page-arrow" onClick={() => { setCurrentPage(totalPages); window.scrollTo({ top: 0 }) }} disabled={currentPage === totalPages} data-tooltip="Last page">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="5 18 11 12 5 6"/><polyline points="13 18 19 12 13 6"/></svg>
          </button>
        </div>
      )}

      {/* ── Modals ─────────────────────────────────────────────────────── */}
      <FilterPanel
        isOpen={showFilterPanel}
        onClose={() => setShowFilterPanel(false)}
        filters={filters}
        setFilters={setFilters}
        bonusAmount={defaultBonusAmount}
        setBonusAmount={setDefaultBonusAmount}
        savedFilters={savedFilters}
        activeFilterId={activeFilterId}
        filterName={filterName}
        setFilterName={setFilterName}
        onSaveFilter={handleSaveFilter}
        onLoadFilter={handleLoadFilter}
        onDeleteFilter={handleDeleteFilter}
        onToggleDefault={handleToggleDefault}
        onReset={resetFilters}
      />

      <HiddenBetsModal
        isOpen={showHiddenModal}
        hiddenBets={hiddenBets}
        onUnhide={unhideOpportunity}
        onUnhideAll={clearAllHidden}
        onClose={() => setShowHiddenModal(false)}
      />

      <TrackBetModal
        opp={track.opp}
        form={track.form}
        onFormChange={track.setForm}
        onConfirm={() => track.save(false)}
        onConfirmAndHide={() => track.save(true)}
        onClose={track.close}
        formatSelection={formatSelection}
      />

      <BonusTour isVisible={isVisible} isMobile={false} />
    </div>
  )
}
